<template>
  <expandable-material-card
    :title="$t('components.portal.download_centre')"
    :color="$vuetify.theme.themes.light.secondary"
    icon="picture_as_pdf"
  >
    <download-centre-categories-table
      :items="categories"
      @update="$emit('update-categories', $event)"
      :systemColors="systemColors"
      :selectedLanguages="selectedLanguages"
    />
    <v-card-title>{{ $t("views.downloadcenter.files") }}</v-card-title>
    <v-card-text>
      <pbas-data-table
        ref="table"
        :showSelect="false"
        :dataProvider="provider"
        :rowActions="rowActions"
        :floatActions="floatActions"
        fileNamePrefix="download-centre"
        identifier="download_centre"
        @error="error = $event"
      ></pbas-data-table>
    </v-card-text>

    <v-dialog scrollable v-model="deleteDialog" persistent max-width="400">
      <material-card
        v-if="editedItem"
        :title="
          $t('components.portal.dashboard_items.delete_{0}', [
            this.editedItem[`Title${this.language.language.toUpperCase()}`],
          ])
        "
        :color="$vuetify.theme.themes.light.error"
        icon="delete"
      >
        <v-card-text>
          {{ $t("globals.are_you_sure") }}
        </v-card-text>
        <v-card-actions>
          <v-btn color text @click="deleteDialog = false">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn color="error" text @click="tryDelete">
            {{ $t("globals.delete") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog scrollable v-model="editDialog" persistent max-width="1200px">
      <material-card
        v-if="editedItem !== null && editedItem !== undefined"
        :title="formTitle"
        :icon="formIcon"
        :color="formColor"
      >
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-container fill-height fluid>
              <v-row wrap>
                <div
                  class="col-12 col-xs-12 col-sm-6 col-md-4"
                  v-for="lang in languages"
                  :key="`FileUrl${lang.language}`"
                >
                  <file-picker
                    :value.sync="
                      editedItem[`FileUrl${lang.language.toUpperCase()}`]
                    "
                    :label="
                      $t(`headers.download_centre.file_url_{0}`, [
                        lang.language.toUpperCase(),
                      ])
                    "
                    :rules="
                      $helpers.isRequired(lang.language, selectedLanguages)
                        ? [$rules.required]
                        : []
                    "
                    :class="
                      $helpers.isRequired(lang.language, selectedLanguages)
                        ? 'required'
                        : ''
                    "
                    :clearable="
                      !$helpers.isRequired(lang.language, selectedLanguages)
                    "
                  ></file-picker>
                </div>
                <div
                  class="col-12 col-xs-12 col-sm-6 col-md-4"
                  v-for="lang in languages"
                  :key="`Title${lang.language}`"
                >
                  <no-autocomplete-textfield
                    v-model="editedItem[`Title${lang.language.toUpperCase()}`]"
                    :label="
                      $t(`headers.left_drawer_menu_items.title_{0}`, [
                        lang.language.toUpperCase(),
                      ])
                    "
                    :rules="
                      $helpers.isRequired(lang.language, selectedLanguages)
                        ? [$rules.required]
                        : []
                    "
                    :class="
                      $helpers.isRequired(lang.language, selectedLanguages)
                        ? 'required'
                        : ''
                    "
                    clearable
                    @click="fillUp"
                  ></no-autocomplete-textfield>
                </div>
                <div class="col-12 col-xs-12 col-sm-6 col-md-4">
                  <v-select
                    v-model="editedItem.Category"
                    :label="$t(`globals.category`)"
                    :items="categories"
                    :rules="[$rules.required]"
                    class="
                      required
                    "
                    :item-text="categoryText"
                    item-value="CategoryEN"
                    return-object
                  ></v-select>
                </div>
                <div class="col-12 col-xs-12 col-sm-6 col-md-4">
                  <v-combobox
                    :value="memberTypes.split(',').filter((e) => e)"
                    @input="memberTypes = $event.filter((e) => e).join(',')"
                    hide-selected
                    :label="$t('globals.member_groups')"
                    :hide-no-data="!inputMemberType"
                    :search-input.sync="inputMemberType"
                    multiple
                    small-chips
                    :rules="[rules.requiredStr]"
                    class="required"
                  >
                    <template
                      v-slot:selection="{ attrs, item, parent, selected }"
                    >
                      <v-chip
                        v-bind="attrs"
                        color="primary"
                        :input-value="selected"
                        label
                        small
                      >
                        <span class="pr-2">
                          {{ item }}
                        </span>
                        <v-icon small @click="parent.selectItem(item)">
                          close
                        </v-icon>
                      </v-chip>
                    </template>
                  </v-combobox>
                </div>
                <div class="col-12 col-xs-12 col-sm-6 col-md-4">
                  <v-checkbox
                    :label="$t('globals.include_benefits')"
                    v-model="hasIncludeBenefits"
                    @change="includeBenefits = $event ? includeBenefits : ''"
                  ></v-checkbox>
                  <v-combobox
                    v-if="hasIncludeBenefits"
                    :value="includeBenefits.split(',').filter((e) => e)"
                    @input="includeBenefits = $event.filter((e) => e).join(',')"
                    hide-selected
                    :label="$t('globals.include_benefits')"
                    :hide-no-data="!inputIncludeBenefit"
                    :search-input.sync="inputIncludeBenefit"
                    multiple
                    small-chips
                    :rules="[rules.requiredStr]"
                    class="required"
                  >
                    <template
                      v-slot:selection="{ attrs, item, parent, selected }"
                    >
                      <v-chip
                        v-bind="attrs"
                        color="primary"
                        :input-value="selected"
                        label
                        small
                      >
                        <span class="pr-2">
                          {{ item }}
                        </span>
                        <v-icon small @click="parent.selectItem(item)">
                          close
                        </v-icon>
                      </v-chip>
                    </template>
                  </v-combobox>
                </div>
                <div class="col-12 col-xs-12 col-sm-6 col-md-4">
                  <v-checkbox
                    :label="$t('globals.exclude_benefits')"
                    v-model="hasExcludeBenefits"
                    @change="excludeBenefits = $event ? excludeBenefits : ''"
                  ></v-checkbox>
                  <v-combobox
                    v-if="hasExcludeBenefits"
                    :value="excludeBenefits.split(',').filter((e) => e)"
                    @input="excludeBenefits = $event.filter((e) => e).join(',')"
                    hide-selected
                    :label="$t('globals.exclude_benefits')"
                    :hide-no-data="!inputExcludeBenefit"
                    :search-input.sync="inputExcludeBenefit"
                    multiple
                    small-chips
                    :rules="[rules.requiredStr]"
                    class="required"
                  >
                    <template
                      v-slot:selection="{ attrs, item, parent, selected }"
                    >
                      <v-chip
                        v-bind="attrs"
                        color="primary"
                        :input-value="selected"
                        label
                        small
                      >
                        <span class="pr-2">
                          {{ item }}
                        </span>
                        <v-icon small @click="parent.selectItem(item)">
                          close
                        </v-icon>
                      </v-chip>
                    </template>
                  </v-combobox>
                </div>
                <div
                  class="col-12 col-xs-12 col-sm-6 col-md-4"
                  v-if="editedItem.DateUploaded"
                >
                  <pbas-date-picker
                    :label="$t('headers.plan_notifications.date_uploaded')"
                    :value="editedItem.DateUploaded"
                    @update-date="editedItem.DateUploaded = $event"
                    :rules="[$rules.required]"
                    class="required"
                    disabled
                  ></pbas-date-picker>
                </div>
                <div class="col-12 col-xs-12 col-sm-6 col-md-4">
                  <pbas-date-picker
                    :label="$t('headers.download_centre.due_date')"
                    :value="editedItem.DueDate"
                    @update-date="editedItem.DueDate = $event"
                    :rules="[$rules.required]"
                    class="required"
                  ></pbas-date-picker>
                </div>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="close">{{ $t("globals.cancel") }}</v-btn>
          <v-btn :disabled="!valid" color="primary" @click="save">
            {{ $t("globals.save") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <error-handler :error="error"></error-handler>
  </expandable-material-card>
</template>

<script>
import { mapGetters } from "vuex";
import ErrorHandler from "@/components/ErrorHandler";
import NoAutocompleteTextfield from "@/components/NoAutocompleteTextfield";
import { InMemoryProvider } from "pbas-vue-components";
import getHeaders from "@/headers/DownloadCentre";
import FilePicker from "./FilePicker.vue";
import ExpandableMaterialCard from "./ExpandableMaterialCard.vue";
import DownloadCentreCategoriesTable from "./DownloadCentreCategoriesTable.vue";

export default {
  components: {
    ErrorHandler,
    NoAutocompleteTextfield,
    FilePicker,
    ExpandableMaterialCard,
    DownloadCentreCategoriesTable,
  },
  props: ["items", "categories", "systemColors", "selectedLanguages"],
  data() {
    return {
      // categories: {
      //   EN: ["Booklets", "Forms", "Brochures"],
      //   FR: ["Pamphlets", "Formes", "Brochures"],
      // },
      error: null,
      deleteDialog: false,
      floatActions: [
        {
          name: "addItem",
          icon: "add",
          color: "#64ba5f",
          text: "globals.add_new",
          handler: () => {
            let now = new Date();
            let day = now.getDate();
            let month = now.getMonth() + 1;
            if (day.toString().length === 1) day = "0" + day.toString();
            if (month.toString().length === 1) month = "0" + month.toString();

            let item = {
              Identifier: Math.random().toString(),
              Email: "",
              DateUploaded:
                now.getFullYear() + "-" + month + "-" + day + "T00:00:00.000Z",
            };
            this.isNew = true;
            this.editItem(item);
          },
        },
      ],
      rowActions: [
        {
          name: "edit",
          icon: "edit",
          title: "globals.edit",
          handler: (item) => {
            this.isNew = false;
            this.editItem(item);
          },
        },
        {
          name: "delete",
          icon: "delete",
          title: "globals.delete",
          handler: (item) => {
            this.editedItem = item;
            this.deleteDialog = true;
          },
        },
      ],
      editDialog: false,
      valid: true,
      editedItem: null,
      isNew: false,
      provider: undefined,
      uploading: false,
      memberTypes: "",
      inputMemberType: "",
      includeBenefits: "",
      inputIncludeBenefit: "",
      hasIncludeBenefits: false,
      excludeBenefits: "",
      inputExcludeBenefit: "",
      hasExcludeBenefits: false,
      rules: {
        requiredStr: (v) => {
          // console.log(v);
          return v.length > 0 || this.$t("validation.required");
        },
      },
    };
  },
  computed: {
    ...mapGetters("common", ["language", "languages"]),
    ...mapGetters("plansMembers", ["options"]),
    ...mapGetters("common", {
      selectedPlan: "plan",
      settings: "settings",
    }),
    formTitle() {
      return this.isNew
        ? this.$t("views.plandownloadcenter.add_file")
        : this.$t("components.portal.dashboard_items.edit_{0}", [
            this.editedItem[`Title${this.language.language.toUpperCase()}`],
          ]);
    },
    formIcon() {
      return this.isNew ? "add" : "edit";
    },
    formColor() {
      return this.isNew ? this.$vuetify.theme.themes.light.success : this.$vuetify.theme.themes.light.primary;
    },
    uploadUrl() {
      return `${this.settings.DOCUMENTSERVICEURL}Api/UploadFilesFormData/{
        this.selectedPlan.UniqueIdentifier
      }_portal`;
    },
  },
  watch: {
    items: {
      handler(v) {
        console.log("@items", v);
        this.loadPlanData();
      },
    },
    // categories:{
    //   handler(v) {
    //     console.log("@cat", v);
    //     this.loadPlanData();
    //   },
    // },
    "editedItem.FileUrlEN": {
      handler(v) {
        if (v !== null && v !== undefined && (this.editedItem.FileUrlFR === null || this.editedItem.FileUrlFR === undefined || this.editedItem.FileUrlFR === "")) {
          this.editedItem.FileUrlFR = v;
        }
      },
    },
    systemColors: {
      handler(v) {
        this.loadPlanData();
      },
    },
  },
  async mounted() {
    this.loadPlanData();
  },
  methods: {
    categoryText(item) {
      return item["Category"+this.language.language.toUpperCase()];
    },
    onUploadCompleted(res) {
      // console.log(res);
      this.uploading = false;
      this.snackbar = true;
      // this.dialogs.file = false;
      // this.refresh();
    },
    onUploadStarted() {
      this.uploading = true;
    },
    onUploadError() {
      this.uploading = false;
    },
    updateItems(items) {
      this.$nextTick(() => {
        this.$emit("update", items);
      });
    },
    fillUp() {
      if (!this.editedItem.TitleFR) this.editedItem.TitleFR = this.editedItem.TitleEN
    },
    loadPlanData() {
      this.$nextTick(()=>{
        let items = [];
        if (this.items !== null && this.items !== undefined) {
          items = this.items.map((o) => {
            o.Identifier = Math.random().toString();
            return o;
          });
        }
        this.provider = new InMemoryProvider(
          items,
          getHeaders(this.languages, this.systemColors)
        );

        if (this.$refs.table) {
          this.$refs.table.getDataAsync();
          this.$forceUpdate();
        }
      });
    },
    async editItem(item) {
      this.editedItem = JSON.parse(JSON.stringify(item));
      if (
        this.editedItem.MemberTypes != null &&
        this.editedItem.MemberTypes !== undefined
      ) {
        this.memberTypes = JSON.stringify(this.editedItem.MemberTypes)
          .replace(/\[/g, "")
          .replace(/\]/g, "")
          .replace(/"/g, "");
      } else {
        this.memberTypes = "";
      }
      if (
        this.editedItem.IncludeBenefits != null &&
        this.editedItem.IncludeBenefits !== undefined && this.editedItem.IncludeBenefits.length
      ) {
        this.includeBenefits = JSON.stringify(this.editedItem.IncludeBenefits)
          .replace(/\[/g, "")
          .replace(/\]/g, "")
          .replace(/"/g, "");
          this.hasIncludeBenefits = true;
      } else {
        this.includeBenefits = "";
        this.hasIncludeBenefits = false;
      }
      if (
        this.editedItem.ExcludeBenefits != null &&
        this.editedItem.ExcludeBenefits !== undefined && this.editedItem.ExcludeBenefits.length
      ) {
        this.excludeBenefits = JSON.stringify(this.editedItem.ExcludeBenefits)
          .replace(/\[/g, "")
          .replace(/\]/g, "")
          .replace(/"/g, "");
          this.hasExcludeBenefits = true;
      } else {
        this.excludeBenefits = "";
        this.hasExcludeBenefits = false;
      }
      this.editedItem.Category = this.categories.filter(o=>o.CategoryEN === this.editedItem.CategoryEN)[0];

      this.editDialog = true;
    },
    close() {
      this.editDialog = false;
      this.editedItem = null;
      this.$refs.form.resetValidation();
    },
    save() {
      this.editedItem.PlanIdentityId = this.planIdentityId;

      this.editedItem.MemberTypes = this.memberTypes.split(",");
      this.editedItem.IncludeBenefits = this.includeBenefits && this.includeBenefits.length ? this.includeBenefits.split(",") : [];
      this.editedItem.ExcludeBenefits = this.excludeBenefits && this.excludeBenefits.length ? this.excludeBenefits.split(",") : [];
      this.editedItem.CategoryEN = this.editedItem.Category.CategoryEN;
      this.editedItem.CategoryFR = this.editedItem.Category.CategoryFR;
      this.editedItem.CategoryIcon = this.editedItem.Category.CategoryIcon;
      this.editedItem.CategoryIconColor = this.editedItem.Category.CategoryIconColor;
      this.editedItem.CategoryTabColor = this.editedItem.Category.CategoryTabColor;
      delete this.editedItem.Category;

      if (this.isNew) {
        this.add();
      } else {
        this.update();
      }
    },
    async update() {
      const item = this.provider.items.filter(
        (o) => o.Identifier === this.editedItem.Identifier
      )[0];
      const index = this.provider.items.indexOf(item);
      this.provider.items[index] = JSON.parse(JSON.stringify(this.editedItem));
      this.close();
      this.$refs.table.getDataAsync();
      this.updateItems(this.provider.items);
    },
    async add() {
      console.log(JSON.stringify(this.editedItem));
      this.provider.items.push(JSON.parse(JSON.stringify(this.editedItem)));
      this.close();
      this.$refs.table.getDataAsync();
      this.updateItems(this.provider.items);
    },
    async tryDelete() {
      console.log("$$$$$$$$$$$$$$$$$$-delete", this.editedItem);
      const item = this.provider.items.filter(
        (o) => o.Identifier === this.editedItem.Identifier
      )[0];
      const index = this.provider.items.indexOf(item);
      this.provider.items.splice(index, 1);
      this.deleteDialog = false;
      this.$refs.table.getDataAsync();
      this.updateItems(this.provider.items);
    },
  },
};
</script>
